import React from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { PrimaryButton, Text, SizeableRoundedDialog, Checkbox } from 'library';
import theme from 'theme';
import useLoading from 'library/hooks/useLoading';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import { ILeanShift } from '../../../BusinessJob/models';
import JobTimePicker from '../../../../../forms/components/pages/DateAndTimePage/JobTimePicker';
import moment from 'moment';
import useForm from 'parent-portal/forms/components/FormHook';
import { IShift } from 'models';
import { createCenterInitiatedAppointmentTimeChangeRequest } from '../businessAppointmentApi';
import { JobTimes } from '../../../../../forms/JobRequestTypes';

export default function EditModalWithTimeChangeRequest({
    open,
    onClose,
    shift,
    providerName,
    refresh,
}: {
    open: boolean;
    onClose: () => void;
    shift: IShift | ILeanShift;
    providerName: string;
    refresh: () => void;
}) {
    const small = useMediaQuery(theme.breakpoints.down('sm'));
    const { loading, setLoading } = useLoading();

    const form = useForm<{
        jobTimes: JobTimes;
        removeProvider: boolean;
    }>({
        jobTimes: { start: moment(shift.start_date), end: moment(shift.end_date) },
        removeProvider: false,
    });

    const { jobTimes, removeProvider } = form.fieldStates;

    function mainText() {
        return `${providerName} will be notified to review these time changes. If they decline, you can choose to remove ${providerName} and we'll look for another worker.`;
    }

    function disclaimerText() {
        return `Note: if ${providerName} does not respond by the shift start time, the times will be kept as the original shift times and ${providerName} will remain on the shift.`;
    }

    function submit() {
        setLoading(true);
        createCenterInitiatedAppointmentTimeChangeRequest({
            appointmentId: shift.id,
            startTime: jobTimes.value.start,
            endTime: jobTimes.value.end,
            onDecline: removeProvider.value ? 'remove_provider' : 'keep_provider',
        })
            .then(refresh)
            .finally(() => {
                setLoading(false);
                onClose();
            });
    }
    return (
        <SizeableRoundedDialog
            open={open}
            onClose={onClose}
            fullScreen={small}
            closeButton
            maxWidth="md"
            style={{ maxWidth: 500 }}
        >
            <Grid container item direction="column" style={{ padding: 20, gap: 10 }}>
                <Text variant="h1">Update times for {formatShortWeekdayMonthDay(shift.start_date)}?</Text>
                <Text>{mainText()}</Text>
                {disclaimerText() && <Text variant="caption">{disclaimerText()}</Text>}
                <JobTimePicker
                    jobTimes={form.fieldStates.jobTimes}
                    jobDate={moment(shift.start_date)}
                    allowPastMidnight={false}
                />
                <Checkbox
                    label={`Remove ${providerName} if they cannot work the new times.`}
                    checked={removeProvider.value}
                    onChange={() => {
                        removeProvider.setValue(!removeProvider.value);
                    }}
                />
                <PrimaryButton
                    disabled={loading}
                    onClick={submit}
                    buttonStyle={{ width: undefined, alignSelf: 'flex-end' }}
                >
                    Submit
                </PrimaryButton>
            </Grid>
        </SizeableRoundedDialog>
    );
}
